import type { FilaDeCartera } from '@/api/cartera.api';
import { enlaceWhatsApp } from './athena';
import { ZONA_DEL_NEGOCIO } from './zona';
import { SEP_DE_VARIAS, type Faceta } from './superfiltro';

/**
 * LA PANTALLA «CLIENTES» (tanda 5, Ola A): buscar, filtrar, ordenar y agrupar la
 * cartera del dueño. PURO: la página (`pages/cartera/Cartera.tsx`) solo lo cablea.
 * Las fechas se leen en la hora del negocio, aunque el panel se abra desde otro país.
 */

const ZONA = ZONA_DEL_NEGOCIO;
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const DIA_MS = 86_400_000;
const COLLATOR = new Intl.Collator('es');

const sinAcentos = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '');
const plano = (s: string | null | undefined) => sinAcentos(s ?? '').toLowerCase();
const porNombre = (a: FilaDeCartera, b: FilaDeCartera) => COLLATOR.compare(a.nombre, b.nombre);
const temperatura = (f: FilaDeCartera) => f.conversacion?.temperatura ?? null;

/** «zoom_agendado» → «Zoom agendado». Para la etapa y el tipo. */
export function legible(clave: string): string {
  const t = clave.replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** La última palabra del nombre: «Ana María Pérez» → «Pérez». Al ordenar se le quitan los acentos. */
export function apellidoDe(nombre: string): string {
  return nombre.trim().split(/\s+/).pop() ?? '';
}

const formatos = new Map<string, Intl.DateTimeFormat>();
/** [año, mes, día] de una fecha en la zona. */
function diaEnZona(fecha: Date, zona: string): [number, number, number] {
  let f = formatos.get(zona);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: zona, year: 'numeric', month: 'numeric', day: 'numeric' });
    formatos.set(zona, f);
  }
  const p = Object.fromEntries(f.formatToParts(fecha).map((x) => [x.type, x.value]));
  return [Number(p.year), Number(p.month), Number(p.day)];
}

/** 'MM-DD' válido → [mes, día]. El 2000 es bisiesto: el 29 de febrero vale. */
function leerMmdd(mmdd: string | null): [number, number] | null {
  const m = mmdd?.match(/^(\d{2})-(\d{2})$/);
  if (!m) return null;
  const mes = Number(m[1]);
  const dia = Number(m[2]);
  return new Date(Date.UTC(2000, mes - 1, dia)).getUTCMonth() === mes - 1 ? [mes, dia] : null;
}

/** Días hasta el próximo cumpleaños, de 0 (hoy) a 365; null sin fecha. */
export function diasHastaCumple(mmdd: string | null, hoy: Date, zona = ZONA): number | null {
  const md = leerMmdd(mmdd);
  if (!md) return null;
  const [y, m, d] = diaEnZona(hoy, zona);
  const desde = Date.UTC(y, m - 1, d);
  // Date.UTC desborda solo: el 29 de febrero de un año no bisiesto cae el 1 de marzo.
  const este = Date.UTC(y, md[0] - 1, md[1]);
  const proximo = este >= desde ? este : Date.UTC(y + 1, md[0] - 1, md[1]);
  return Math.round((proximo - desde) / DIA_MS);
}

/** 'MM-DD' → «14 mar». */
export function cumpleLegible(mmdd: string | null): string | null {
  const md = leerMmdd(mmdd);
  return md ? `${md[1]} ${MESES[md[0] - 1]}` : null;
}

/** ISO → «5 oct 2026», en la hora del negocio. */
export function fechaLegible(iso: string, zona = ZONA): string {
  const [y, m, d] = diaEnZona(new Date(iso), zona);
  return `${d} ${MESES[m - 1]} ${y}`;
}

export type Filtro = 'todos' | 'cartera' | 'prospectos' | 'futuro' | 'parejas' | 'solos' | 'patrocinio' | 'importados' | 'personales';

const esTipo = (tipo: string) => (f: FilaDeCartera) => plano(f.tipo) === tipo;
/** Ola E: la familia y los amigos («Es personal») no son clientes: no cuentan en las listas de venta. */
const deVenta = (cumple: (f: FilaDeCartera) => boolean) => (f: FilaDeCartera) => !f.personal && cumple(f);

export const FILTROS: ReadonlyArray<{ id: Filtro; etiqueta: string; cumple: (f: FilaDeCartera) => boolean }> = [
  { id: 'todos', etiqueta: 'Todos', cumple: () => true },
  { id: 'cartera', etiqueta: 'Ya tienen su entrada', cumple: deVenta((f) => temperatura(f) === 'CERRADO') },
  { id: 'prospectos', etiqueta: 'Prospectos', cumple: deVenta((f) => ['CALIENTE', 'TIBIO'].includes(temperatura(f) ?? '')) },
  { id: 'futuro', etiqueta: 'Para más adelante', cumple: deVenta((f) => ['FRIO', 'LARGO_PLAZO'].includes(temperatura(f) ?? '')) },
  { id: 'parejas', etiqueta: 'Parejas', cumple: esTipo('pareja') },
  { id: 'solos', etiqueta: 'Escriben solos', cumple: esTipo('una_persona') },
  { id: 'patrocinio', etiqueta: 'Patrocinio', cumple: esTipo('patrocinador') },
  // Del Excel y todavía sin una conversación con mensajes: los del primer toque.
  { id: 'importados', etiqueta: 'Importados sin chat', cumple: deVenta((f) => f.origen === 'importado' && !f.conversacion?.ultimoMensajeEl) },
  { id: 'personales', etiqueta: 'Personales', cumple: (f) => f.personal === true },
];

/** Lo que está filtrado, para decirlo junto al botón «Filtro»: «Prospectos · Tour · Promo octubre». null = no hay nada puesto. */
export function resumenDeFiltro(filtro: Filtro, etapa: string, fuentes: readonly string[] = []): string | null {
  const deFuentes = fuentes.length > 2 ? `${fuentes.length} fuentes` : fuentes.join(', ');
  const partes = [filtro !== 'todos' && FILTROS.find((f) => f.id === filtro)?.etiqueta, etapa && legible(etapa), deFuentes].filter(Boolean);
  return partes.length ? partes.join(' · ') : null;
}

export interface Criterio {
  filtro: Filtro;
  etapa?: string;
  /** Las etapas de la ficha, en orden: la primera recibe también a los que no tienen etapa (igual que `posicionDe`, en `lib/proceso`). */
  etapas?: string[];
  /** T14: solo los de estas fuentes (vacío o sin poner = todas). */
  fuentes?: readonly string[];
  busqueda?: string;
}

/** La búsqueda ignora acentos y mayúsculas: nombre, teléfono (solo los dígitos), email, país, nacionalidad y notas. */
export function filtrar(filas: FilaDeCartera[], { filtro, etapa, etapas = [], fuentes = [], busqueda }: Criterio): FilaDeCartera[] {
  const cumple = FILTROS.find((x) => x.id === filtro)?.cumple ?? (() => true);
  const q = plano(busqueda).trim();
  const digitos = q.replace(/\D/g, '');
  return filas.filter(
    (f) =>
      cumple(f) &&
      (fuentes.length === 0 || fuentes.includes(f.fuente)) &&
      (!etapa || f.etapa === etapa || (etapa === etapas[0] && !(f.etapa && etapas.includes(f.etapa)))) &&
      (!q ||
        [f.nombre, f.email, f.pais, f.nacionalidad, f.notas].some((t) => plano(t).includes(q)) ||
        (digitos !== '' && (f.telefono ?? '').replace(/\D/g, '').includes(digitos))),
  );
}

export type Orden = 'nombre' | 'apellido' | 'cumple' | 'nuevos' | 'viejos';

export const ORDENES: ReadonlyArray<{ id: Orden; etiqueta: string }> = [
  { id: 'nombre', etiqueta: 'Nombre' },
  { id: 'apellido', etiqueta: 'Apellido' },
  { id: 'cumple', etiqueta: 'Próximo cumpleaños' },
  { id: 'nuevos', etiqueta: 'Más nuevos' },
  { id: 'viejos', etiqueta: 'Más viejos' },
];

/** Una copia ordenada. Los empates, por nombre. */
export function ordenar(filas: FilaDeCartera[], orden: Orden, hoy: Date): FilaDeCartera[] {
  const copia = [...filas];
  switch (orden) {
    case 'apellido': {
      const clave = new Map(copia.map((f) => [f, sinAcentos(apellidoDe(f.nombre))]));
      return copia.sort((a, b) => COLLATOR.compare(clave.get(a)!, clave.get(b)!) || porNombre(a, b));
    }
    case 'cumple': {
      // Sin fecha, al final; empate (o los dos sin fecha) por nombre.
      const dias = new Map(copia.map((f) => [f, diasHastaCumple(f.cumpleanos, hoy) ?? Infinity]));
      return copia.sort((a, b) => {
        const da = dias.get(a)!;
        const db = dias.get(b)!;
        return da === db ? porNombre(a, b) : da < db ? -1 : 1;
      });
    }
    case 'nuevos':
      return copia.sort((a, b) => Date.parse(b.entroEl) - Date.parse(a.entroEl) || porNombre(a, b));
    case 'viejos':
      return copia.sort((a, b) => Date.parse(a.entroEl) - Date.parse(b.entroEl) || porNombre(a, b));
    default:
      return copia.sort(porNombre);
  }
}

/** La inicial sin acentos, en mayúscula; lo que no empieza por letra, «#». */
export function inicialDe(texto: string): string {
  const l = sinAcentos(texto.trim()).charAt(0).toUpperCase();
  return /^[A-Z]$/.test(l) ? l : '#';
}

export interface GrupoPorLetra {
  letra: string;
  filas: FilaDeCartera[];
}

/** Grupos A–Z por la inicial del nombre (o del apellido); «#» al final. Dentro, el orden que traen. */
export function porLetra(filas: FilaDeCartera[], porApellido = false): GrupoPorLetra[] {
  const grupos = new Map<string, FilaDeCartera[]>();
  for (const f of filas) {
    const letra = inicialDe(porApellido ? apellidoDe(f.nombre) : f.nombre);
    const g = grupos.get(letra);
    if (g) g.push(f);
    else grupos.set(letra, [f]);
  }
  return [...grupos]
    .map(([letra, filas]) => ({ letra, filas }))
    .sort((a, b) => Number(a.letra === '#') - Number(b.letra === '#') || (a.letra < b.letra ? -1 : a.letra > b.letra ? 1 : 0));
}

/**
 * El enlace para llamarlo, o null sin teléfono. Con más de 10 dígitos trae el código de
 * país (así llegan los de WhatsApp) y lleva «+»; con 10 o menos se marca como está escrito.
 */
export function enlaceTel(telefono: string | null): string | null {
  const digitos = (telefono ?? '').replace(/\D/g, '');
  if (digitos.length < 7) return null;
  return `tel:${digitos.length > 10 ? '+' : ''}${digitos}`;
}

/**
 * A dónde lleva «Chat»: su conversación en el panel; sin conversación y con teléfono,
 * su WhatsApp (solo lo abre: no manda nada); sin ninguna de las dos, a ninguna parte.
 */
export function chatDe(fila: Pick<FilaDeCartera, 'conversacion' | 'telefono'>): { ruta: string } | { url: string } | null {
  if (fila.conversacion) return { ruta: `/conversations/${fila.conversacion.id}` };
  const url = enlaceWhatsApp(fila.telefono);
  return url ? { url } : null;
}

/**
 * Revisión final t5 (M5): «Asignar → Agente de IA» a un contacto personal no lo atiende
 * nadie (el motor no le contesta). El aviso en vez de asignar, o null si se puede.
 */
export function avisoAlAsignar(fila: Pick<FilaDeCartera, 'personal'>, valor: string): string | null {
  return fila.personal && valor.startsWith('agente:') ? 'Es un contacto personal: el agente no le escribe. Quítalo de personales primero.' : null;
}

/** A quién está asignado hoy el chat, dicho paral dueño. */
export function asignadoA(
  conv: NonNullable<FilaDeCartera['conversacion']>,
  { yoId, agentes, personas }: { yoId?: string; agentes: Array<{ id: string; name: string }>; personas: Array<{ id: string; name?: string | null; email: string }> },
): string {
  if (conv.personaId) {
    if (conv.personaId === yoId) return 'Lo atiendes tú';
    const p = personas.find((x) => x.id === conv.personaId);
    return p ? `Lo atiende ${p.name || p.email}` : 'Lo atiende una persona del equipo';
  }
  if (conv.estado === 'WAITING_HUMAN') return 'Espera a una persona del equipo';
  // Sin agente propio contesta el del canal: igual es la IA.
  const a = agentes.find((x) => x.id === conv.agenteId);
  return a ? `Lo atiende la IA (${a.name})` : 'Lo atiende la IA';
}

/** T14: las fuentes que hay en la lista, con cuántos clientes trae cada una; de la más grande a la más chica. */
export function fuentesDe(filas: readonly FilaDeCartera[]): Array<{ fuente: string; cuantos: number }> {
  const cuenta = new Map<string, number>();
  for (const f of filas) cuenta.set(f.fuente, (cuenta.get(f.fuente) ?? 0) + 1);
  return [...cuenta].map(([fuente, cuantos]) => ({ fuente, cuantos })).sort((a, b) => b.cuantos - a.cuantos || COLLATOR.compare(a.fuente, b.fuente));
}

/** Poner o quitar una fuente del filtro. */
export function alternarFuente(fuentes: readonly string[], fuente: string): string[] {
  return fuentes.includes(fuente) ? fuentes.filter((x) => x !== fuente) : [...fuentes, fuente];
}

/**
 * Las facetas del superfiltro de Clientes: a quiénes (con cuántos hay en cada uno), la etapa y
 * (T14) la fuente, de la que se eligen varias a la vez. PURO.
 */
export function facetasDeClientes(
  etapas: readonly string[],
  cuentas: Partial<Record<Filtro, number>>,
  fuentes: ReadonlyArray<{ fuente: string; cuantos: number }> = [],
): Faceta[] {
  return [
    {
      clave: 'quienes',
      titulo: 'Quiénes',
      todas: FILTROS.find((f) => f.id === 'todos')?.etiqueta,
      opciones: FILTROS.filter((f) => f.id !== 'todos').map((f) => ({ valor: f.id, etiqueta: f.etiqueta, cuenta: cuentas[f.id] ?? 0 })),
    },
    ...(etapas.length > 0 ? [{ clave: 'etapa', titulo: 'Etapa', todas: 'Todas', opciones: etapas.map((e) => ({ valor: e, etiqueta: legible(e) })) }] : []),
    // Siempre: sin fuentes no se dibuja, salvo que traiga una puesta (una vista guardada), para poderla quitar.
    { clave: 'fuente', titulo: 'Fuente', todas: 'Todas', varias: true, opciones: fuentes.map((f) => ({ valor: f.fuente, etiqueta: f.fuente, cuenta: f.cuantos })) },
  ];
}

/** Las fuentes elegidas como el valor de la faceta «Fuente» (varias juntas en un solo texto). */
export const valorDeFuentes = (fuentes: readonly string[]): string | undefined => (fuentes.length > 0 ? fuentes.join(SEP_DE_VARIAS) : undefined);
